/**
 * Reserved slugs for organization creation.
 *
 * RESERVED_SLUGS: hard-blocked. These collide with top-level routes in
 * app/ or with framework/SEO assets. Source of truth duplicated in the
 * provision_organization RPC (supabase/migrations/20260607000000_*.sql)
 * as a defense-in-depth measure.
 *
 * KNOWN_INSTITUTION_SLUGS: not blocked. Used to flag the resulting
 * organization for manual review when the slug matches but the creator's
 * email domain doesn't plausibly correspond. Provisioning still succeeds;
 * we set slug_review_status = 'flagged' so a human can verify.
 */

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // App route segments (must match top-level /app/* directories)
  'admin',
  'api',
  'auth',
  'for',
  'get-app',
  'in',
  'join',
  'login',
  'logout',
  'offline',
  'onboarding',
  'payment',
  'policies',
  'pricing',
  'profile',
  'requests',
  'settings',
  'setup',
  'signup-incomplete',
  'forgot-password',
  'start',
  // System / reserved
  'default',
  'www',
  'help',
  'docs',
  'support',
  'about',
  'status',
  'blog',
  'app',
  'mail',
  'email',
  'public',
  'static',
  'assets',
  'favicon',
  'robots',
  'sitemap',
  'sw',
  'manifest',
  'opengraph',
  'apple-icon',
  'icon',
  'globals',
  'not-found',
  'error',
])

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug.toLowerCase())
}

/**
 * Lightweight institution-name heuristic. When the requested slug matches one
 * of these AND the creator's email domain does not contain the slug,
 * provisioning still succeeds but slug_review_status is set to 'flagged'
 * for manual review. Not a security control — a noise-reduction signal.
 */
const KNOWN_INSTITUTION_SLUGS: ReadonlySet<string> = new Set([
  'harvard',
  'stanford',
  'mit',
  'yale',
  'princeton',
  'columbia',
  'cornell',
  'dartmouth',
  'upenn',
  'brown',
  'caltech',
  'berkeley',
  'ucla',
  'oxford',
  'cambridge',
  'lse',
  'imperial',
  'eth',
  'epfl',
  'insead',
  'wharton',
  'kellogg',
  'sloan',
  'booth',
  'hbs',
  'gsb',
  'iese',
  'iesa',
  'iae',
  'utdt',
])

export function computeSlugReviewStatus(slug: string, email: string): 'auto_approved' | 'flagged' {
  const normalizedSlug = slug.toLowerCase()
  if (!KNOWN_INSTITUTION_SLUGS.has(normalizedSlug)) {
    return 'auto_approved'
  }
  const domain = email.split('@')[1]?.toLowerCase() ?? ''
  // If the email domain plausibly relates to the slug, accept.
  // e.g. slug "harvard" + email "@harvard.edu" → auto_approved.
  if (domain.includes(normalizedSlug)) {
    return 'auto_approved'
  }
  return 'flagged'
}

export function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/
